import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

interface ExpiringConnection {
  platform: 'linkedin' | 'facebook' | 'instagram' | 'twitter';
  daysUntilExpiry: number;
  expiresAt: Date;
}

/**
 * Hook to check if any social media connections are expiring soon
 * Returns connections that expire within 7 days
 */
export const useSocialMediaExpiration = (userId: string | null) => {
  const [expiringConnections, setExpiringConnections] = useState<ExpiringConnection[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setExpiringConnections([]);
      setLoading(false);
      return;
    }

    const checkExpiration = async () => {
      try {
        const connectionRef = doc(db, 'socialConnections', userId);
        const connectionDoc = await getDoc(connectionRef);

        if (!connectionDoc.exists()) {
          setExpiringConnections([]);
          setLoading(false);
          return;
        }

        const data = connectionDoc.data();
        const expiring: ExpiringConnection[] = [];

        // Check each platform
        const platforms: Array<'linkedin' | 'facebook' | 'instagram' | 'twitter'> = [
          'linkedin',
          'facebook',
          'instagram',
          'twitter',
        ];

        const now = new Date();
        const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

        platforms.forEach((platform) => {
          const platformData = data[platform];
          
          if (platformData && platformData.connected && platformData.expiresAt) {
            const expiresAt = platformData.expiresAt.toDate();
            const daysUntilExpiry = Math.ceil(
              (expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
            );

            // Include if expires within 7 days and not yet expired
            if (daysUntilExpiry <= 7 && daysUntilExpiry >= 0) {
              expiring.push({
                platform,
                daysUntilExpiry,
                expiresAt,
              });
            }
          }
        });

        // Sort by urgency (soonest first)
        expiring.sort((a, b) => a.daysUntilExpiry - b.daysUntilExpiry);

        setExpiringConnections(expiring);
        setLoading(false);
      } catch (error) {
        console.error('Error checking social media expiration:', error);
        setExpiringConnections([]);
        setLoading(false);
      }
    };

    checkExpiration();

    // Recheck every hour
    const interval = setInterval(checkExpiration, 60 * 60 * 1000);

    return () => clearInterval(interval);
  }, [userId]);

  return { expiringConnections, loading };
};























